import { Auth0Provider, useAuth0 } from '@auth0/auth0-react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from '@tanstack/react-router';
import { lazy, Suspense, useState } from 'react';
import { UnleashProvider } from '@alchemer/unleash';
import { SentryErrorBoundary } from './components/Error/SentryErrorBoundary/SentryErrorBoundary';
import { AUTH_CONFIG } from './lib/auth0';
import { AuthProvider } from './components/Authentication/AuthProvider';
import { useAuthTokenBootstrap } from './components/Authentication/hooks/useAuthTokenBootstrap';
import { AuthError } from './components/Authentication/AuthError';
import { useInactivityLogout } from './hooks/useInactivityLogout';
import { useLogout } from './hooks/useLogout';
import { useThoughtspotAnswerIds } from './hooks/useThoughtspotAnswerIds';
import { useUnleashUserContext } from './hooks/useUnleashUserContext';
import IdleWarningDialog from './components/IdleWarningDialog/IdleWarningDialog';
import type { AppRouter } from './router';
import type { AppAuthContext } from './types/auth';
import { tokenStorage } from './lib/tokenStorage';
import { getGetSdkTokenQueryKey } from './api/generated/endpoints/dashboard';
import { DEFAULT_QUERY_RETRY_COUNT, DEFAULT_QUERY_STALE_TIME } from './constants/queries';

const DebugTools = import.meta.env.DEV ? lazy(() => import('./components/DebugTools/DebugTools')) : null;
const ReactQueryDevtools = import.meta.env.DEV
  ? lazy(() => import('@tanstack/react-query-devtools').then((res) => ({ default: res.ReactQueryDevtools })))
  : null;

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: DEFAULT_QUERY_STALE_TIME, retry: DEFAULT_QUERY_RETRY_COUNT },
  },
});

// Hydrate React Query cache with TS SDK Token from sessionStorage on app initialization if token exists AND is still valid (not expired)
const cachedToken = tokenStorage.getToken();
if (cachedToken && tokenStorage.isTokenValid()) {
  queryClient.setQueryData(getGetSdkTokenQueryKey(), cachedToken);
} else if (cachedToken && !tokenStorage.isTokenValid()) {
  // Token exists in storage but is expired - clear it
  tokenStorage.clearToken();
}
interface InnerAppProps {
  router: AppRouter;
  authOverride?: AppAuthContext;
}

const LoadingLogo = () => {
  const [imageLoaded, setImageLoaded] = useState(false);

  return (
    <div
      className="flex h-screen items-center justify-center"
      aria-label="Initializing application"
      role="status"
      aria-live="polite"
    >
      <img
        src="/logofinal.png"
        alt="Alchemer Logo"
        onLoad={() => setImageLoaded(true)}
        className={`transition-opacity duration-500 ${imageLoaded ? 'opacity-100' : 'opacity-0'}`}
      />
      <span className="sr-only">Loading application, please wait...</span>
    </div>
  );
};

// Unleash wrapper to initialize context with Auth0 user info
const UnleashWrapper = ({ children }: { children: React.ReactNode }) => {
  return (
    <UnleashProvider
      appName="alchemer-clarity"
      url={import.meta.env.VITE_UNLEASH_URL}
      clientKey={import.meta.env.VITE_UNLEASH_CLIENT_KEY}
      enableToolbar={import.meta.env.DEV}
    >
      {children}
    </UnleashProvider>
  );
};

const InnerApp = ({ router, authOverride }: InnerAppProps) => {
  const baseAuth = useAuth0();
  const raw = authOverride ?? baseAuth;

  const auth: AppAuthContext = {
    isAuthenticated: raw.isAuthenticated,
    isLoading: raw.isLoading,
    user: raw.user as any,
    login: (options) => (raw.loginWithRedirect ? raw.loginWithRedirect(options) : Promise.resolve()),
    loginWithRedirect: raw.loginWithRedirect,
    logout: (options) => raw.logout(options),
    getAccessTokenSilently: raw.getAccessTokenSilently,
  };

  const { authTokenReady, authTokenError, retry } = useAuthTokenBootstrap(auth);
  const handleLogout = useLogout();
  useThoughtspotAnswerIds();
  useUnleashUserContext(auth);
  const { isWarningOpen, remainingSeconds, stayActive } = useInactivityLogout(handleLogout);

  const loading =
    auth.isLoading || !auth.isAuthenticated || (auth.isAuthenticated && !authTokenReady && !authTokenError);

  if (loading) {
    return (
      <div className="animate-in fade-in duration-300">
        <LoadingLogo />
      </div>
    );
  }

  if (authTokenError) {
    return (
      <div className="animate-in fade-in duration-300">
        <AuthError
          message={authTokenError.message}
          retry={retry}
        />
      </div>
    );
  }

  return (
    <div className="animate-in fade-in duration-500">
      <RouterProvider
        router={router}
        context={{ auth, queryClient }}
      />
      <IdleWarningDialog
        open={isWarningOpen}
        remainingSeconds={remainingSeconds}
        onStayActive={stayActive}
        onLogout={handleLogout}
      />
      <Suspense fallback={null}>
        {DebugTools && <DebugTools />}
        {ReactQueryDevtools && <ReactQueryDevtools initialIsOpen={false} />}
      </Suspense>
    </div>
  );
};

const App = ({ router }: { router: AppRouter }) => {
  const onRedirectCallback = (appState: { returnTo?: string } | undefined) => {
    const target = appState?.returnTo || '/';
    router.navigate({ to: target });
  };

  return (
    <SentryErrorBoundary>
      <Auth0Provider
        authorizationParams={AUTH_CONFIG}
        clientId={import.meta.env.VITE_AUTH0_CLIENT_ID}
        domain={import.meta.env.VITE_AUTH0_DOMAIN}
        onRedirectCallback={onRedirectCallback}
      >
        <UnleashWrapper>
          <QueryClientProvider client={queryClient}>
            <AuthProvider>
              {(auth) => (
                <InnerApp
                  router={router}
                  authOverride={auth}
                />
              )}
            </AuthProvider>
          </QueryClientProvider>
        </UnleashWrapper>
      </Auth0Provider>
    </SentryErrorBoundary>
  );
};

export default App;
